/**
 * 🔔 ProactiveGuidePopup - Messages proactifs des guides Heartbeats
 * Mode Mozart - Les guides contactent les utilisateurs automatiquement
 * ✅ Clique sur le message pour aller directement à la section à compléter
 * 🔌 TEMPS RÉEL: Écoute les événements WebSocket pour afficher les messages instantanément
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageCircle, Heart, Sparkles, ArrowRight, Camera, Pencil, Zap } from 'lucide-react';
import { useRealTimeEvent } from '../hooks/useRealTime';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Couleurs et styles par guide
const GUIDE_STYLES = {
  luna: {
    gradient: 'from-purple-600 to-indigo-800',
    border: 'border-purple-400',
    text: 'text-purple-100',
    icon: '🌙',
    glow: 'shadow-purple-500/50'
  },
  enzo: {
    gradient: 'from-red-500 to-orange-600',
    border: 'border-red-400',
    text: 'text-red-100',
    icon: '🔥',
    glow: 'shadow-red-500/50'
  },
  nyx: {
    gradient: 'from-gray-800 to-black',
    border: 'border-gray-600',
    text: 'text-gray-100',
    icon: '🖤',
    glow: 'shadow-gray-500/50'
  },
  kurson: {
    gradient: 'from-yellow-500 to-amber-600',
    border: 'border-yellow-400',
    text: 'text-yellow-100',
    icon: '🎰',
    glow: 'shadow-yellow-500/50'
  }
};

// 🎯 Actions spécifiques par type de trigger
const TRIGGER_ACTIONS = {
  profile_incomplete: {
    label: "Compléter mon profil",
    icon: ArrowRight,
    action: 'EDIT_PROFILE'
  },
  no_photo: {
    label: "Ajouter ma photo",
    icon: Camera,
    action: 'ADD_PHOTO'
  },
  no_bio: {
    label: "Écrire ma bio",
    icon: Pencil,
    action: 'ADD_BIO'
  },
  no_passions: {
    label: "Ajouter mes passions",
    icon: Zap,
    action: 'ADD_PASSIONS'
  }
};

// 🆕 Mapper les éléments manquants vers des actions plus précises
const getMissingItemAction = (missingItem) => {
  if (!missingItem) return null;
  if (missingItem.includes('photo')) return { label: "Ajouter ma photo 📸", icon: Camera, action: 'ADD_PHOTO' };
  if (missingItem.includes('bio')) return { label: "Écrire ma bio ✍️", icon: Pencil, action: 'ADD_BIO' };
  if (missingItem.includes('passions')) return { label: "Ajouter mes passions 💫", icon: Zap, action: 'ADD_PASSIONS' };
  if (missingItem.includes('avatar')) return { label: "Créer mon avatar IA ✨", icon: Sparkles, action: 'CREATE_AVATAR' };
  if (missingItem.includes('prénom')) return { label: "Ajouter mon prénom 👤", icon: ArrowRight, action: 'EDIT_PROFILE' };
  return { label: "Compléter mon profil", icon: ArrowRight, action: 'EDIT_PROFILE' };
};

const ProactiveGuidePopup = ({ customerId, customerName, onOpenGuideChat, onAction }) => {
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  const showProactiveMessage = useCallback((msg) => {
    setCurrentMessage(msg);
    setIsVisible(true);
  }, []);

  // 🔌 TEMPS RÉEL: Écouter les messages des guides via WebSocket
  useRealTimeEvent('guide_message', useCallback((data) => {
    console.log('🔌 Guide message reçu en temps réel:', data);
    showProactiveMessage({
      guide: {
        id: data.guideId,
        name: data.guideName,
        emoji: data.guideEmoji
      },
      message: data.message,
      triggerId: data.triggerId,
      timestamp: data.timestamp
    });
  }, [showProactiveMessage]));

  const checkProactiveMessages = useCallback(async () => {
    try {
      // D'abord, vérifier les déclencheurs potentiels
      const checkRes = await fetch(`${API_URL}/api/guides/proactive/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId, customerName })
      });
      
      if (checkRes.ok) {
        const checkData = await checkRes.json();
        
        // Si des déclencheurs sont en attente, en activer un
        if (checkData.triggers && checkData.triggers.length > 0) {
          const firstTrigger = checkData.triggers[0];
          
          // Déclencher le message
          const triggerRes = await fetch(`${API_URL}/api/guides/proactive/trigger`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              customerId,
              customerName,
              triggerId: firstTrigger.triggerId,
              extraData: firstTrigger.data
            })
          });
          
          if (triggerRes.ok) {
            const triggerData = await triggerRes.json();
            if (triggerData.success) {
              // Afficher le message proactif avec le triggerId
              showProactiveMessage({
                guide: triggerData.guide,
                message: triggerData.message,
                triggerId: triggerData.triggerId
              });
            }
          }
        }
      }
      
      // Aussi récupérer les messages non lus existants
      const pendingRes = await fetch(`${API_URL}/api/guides/proactive/pending/${customerId}`);
      if (pendingRes.ok) {
        const pendingData = await pendingRes.json();
        if (pendingData.messages && pendingData.messages.length > 0) {
          setMessages(pendingData.messages);
          // Afficher le premier message non lu si aucun message actif
          if (pendingData.messages[0]) {
            showProactiveMessage(pendingData.messages[0]);
          }
        }
      }
    } catch (err) {
      console.error('Erreur check proactive:', err);
    }
  }, [customerId, customerName, showProactiveMessage]);

  // Vérifier les messages proactifs au chargement
  useEffect(() => {
    if (customerId && !hasChecked) {
      // Utiliser setTimeout pour éviter l'erreur de setState dans useEffect
      const timer = setTimeout(() => {
        setHasChecked(true);
        checkProactiveMessages();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [customerId, hasChecked, checkProactiveMessages]);

  // Vérifier aussi périodiquement (toutes les 5 min)
  useEffect(() => {
    if (!customerId) return;
    
    const interval = setInterval(() => {
      checkProactiveMessages();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [customerId, checkProactiveMessages]);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      setCurrentMessage(null);
      // Afficher le prochain message s'il y en a
      if (messages.length > 1) {
        const remaining = messages.slice(1);
        setMessages(remaining);
        if (remaining[0]) {
          setTimeout(() => showProactiveMessage(remaining[0]), 500);
        }
      }
    }, 300);
  };

  const handleOpenChat = () => {
    if (currentMessage?.guide?.id && onOpenGuideChat) {
      onOpenGuideChat(currentMessage.guide.id);
    }
    handleDismiss();
  };

  // 🎯 Action: Aller directement à la section concernée
  const handleTriggerAction = () => {
    // 🆕 Utiliser les données "missing" pour une action plus précise
    const missingItems = currentMessage?.data?.missing || [];
    const firstMissing = missingItems[0];
    const specificAction = getMissingItemAction(firstMissing);
    
    if (specificAction && onAction) {
      onAction(specificAction.action);
    } else if (onAction) {
      // Par défaut, ouvrir l'éditeur de profil
      onAction('EDIT_PROFILE');
    }
    
    handleDismiss();
  };

  if (!currentMessage || !isVisible) return null;

  const guideId = currentMessage.guide?.id || 'luna';
  const style = GUIDE_STYLES[guideId] || GUIDE_STYLES.luna;
  const triggerId = currentMessage?.triggerId;
  
  // 🆕 Déterminer l'action spécifique basée sur ce qui manque
  const missingItems = currentMessage?.data?.missing || [];
  const firstMissing = missingItems[0];
  const triggerAction = getMissingItemAction(firstMissing) || TRIGGER_ACTIONS[triggerId];

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100, scale: 0.8 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 100, scale: 0.8 }}
        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        className={`fixed bottom-24 right-4 z-50 max-w-sm w-full mx-4`}
        data-testid="proactive-guide-popup"
      >
        <div className={`relative bg-gradient-to-br ${style.gradient} rounded-2xl shadow-2xl ${style.glow} border ${style.border} overflow-hidden`}>
          {/* Particules décoratives */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <Sparkles className="absolute top-2 right-8 w-4 h-4 text-white/30 animate-pulse" />
            <Heart className="absolute bottom-4 left-4 w-3 h-3 text-white/20 animate-bounce" />
          </div>
          
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <span className="text-2xl">{currentMessage.guide?.emoji || style.icon}</span>
              <div>
                <h4 className={`font-bold ${style.text}`}>
                  {currentMessage.guide?.name || 'Guide'}
                </h4>
                <p className="text-xs text-white/60">Message pour toi ✨</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 rounded-full hover:bg-white/10 transition-colors"
              data-testid="proactive-dismiss-btn"
            >
              <X className="w-5 h-5 text-white/70" />
            </button>
          </div>
          
          {/* Message - CLIQUABLE pour aller à la section */}
          <button
            onClick={handleTriggerAction}
            className="w-full p-4 text-left hover:bg-white/5 transition-colors cursor-pointer"
            data-testid="proactive-message-action"
          >
            <p className={`${style.text} text-sm leading-relaxed`}>
              {currentMessage.message}
            </p>
            {triggerAction && (
              <div className="flex items-center gap-2 mt-3 text-white/80 text-xs">
                <triggerAction.icon className="w-4 h-4" />
                <span className="font-medium">{triggerAction.label}</span>
                <ArrowRight className="w-3 h-3 ml-auto" />
              </div>
            )}
          </button>
          
          {/* Actions */}
          <div className="flex gap-2 p-3 pt-0">
            {triggerAction && (
              <button
                onClick={handleTriggerAction}
                className="flex-1 flex items-center justify-center gap-2 bg-white text-gray-900 py-2 px-4 rounded-xl transition-colors text-sm font-bold hover:bg-white/90"
                data-testid="proactive-action-btn"
              >
                <triggerAction.icon className="w-4 h-4" />
                {triggerAction.label}
              </button>
            )}
            <button
              onClick={handleOpenChat}
              className={`${triggerAction ? '' : 'flex-1'} flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white py-2 px-4 rounded-xl transition-colors text-sm font-medium`}
              data-testid="proactive-reply-btn"
            >
              <MessageCircle className="w-4 h-4" />
              {triggerAction ? '' : 'Répondre'}
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2 text-white/60 hover:text-white text-sm transition-colors"
              data-testid="proactive-later-btn"
            >
              Plus tard
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProactiveGuidePopup;
